import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  GripVertical,
  Loader2,
  Palette,
  Plus,
  RotateCcw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GenieLoader } from '@/components/ui/GenieLoader';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  usePortal,
  usePortalGenies,
  useUpdatePortal,
  useAddGenieToPortal,
  useRemoveGenieFromPortal,
  useReorderPortalGenies,
} from '@/hooks/portal';
import { useAgents } from '@/hooks/agent';
import { PortalGenie, UpdatePortalParams } from '@/types/portal/portal';
import { PortalService } from '@/services/database/PortalService';
import { GenieAvatar } from '@/components/GenieAvatar';
import { BrandingEditor } from '@/components/branding/BrandingEditor';
import { DEFAULT_BRANDING, GenieBranding } from '@/types/ui/branding';
import { useAccountBranding } from '@/hooks/branding/useAccountBranding';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { OverviewSettingRow } from '@/components/agent/overview/OverviewSettingRow';

const PortalDetailPage = () => {
  const navigate = useNavigate();
  const { portalId } = useParams<{ portalId: string }>();
  const { data: portal, isLoading: portalLoading } = usePortal(portalId || '');
  const { data: portalGenies = [], isLoading: geniesLoading } = usePortalGenies(portalId || '');
  const { mutate: updatePortal, isPending: isUpdating } = useUpdatePortal();
  const { mutate: addGenie, isPending: isAdding } = useAddGenieToPortal();
  const { mutate: removeGenie, isPending: isRemoving } = useRemoveGenieFromPortal();
  const { mutate: reorderGenies } = useReorderPortalGenies();

  // Form state
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [columns, setColumns] = useState<'2' | '3' | '4'>('3');

  // Edit mode states
  const [isEditingBasic, setIsEditingBasic] = useState(false);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const [isEditingBranding, setIsEditingBranding] = useState(false);

  // Branding state
  const { branding: accountBranding, isLoading: accountBrandingLoading } = useAccountBranding();
  const [portalBranding, setPortalBranding] = useState<GenieBranding>(DEFAULT_BRANDING);
  const [useAccountBrandingToggle, setUseAccountBrandingToggle] = useState(true);

  // Genie search
  const [genieSearchTerm, setGenieSearchTerm] = useState('');
  const { data: agentsData } = useAgents(100);
  const allAgents = agentsData?.allAgents || [];

  // Copy state
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  // Brand color (default to primary blue)
  const brand = '#4E9CFF';

  // Initialize form when portal loads
  useEffect(() => {
    if (portal) {
      setName(portal.name);
      setSlug(portal.slug);
      setDescription(portal.description || '');
      setWelcomeMessage(portal.welcome_message || '');
      setIsActive(portal.is_active);
      setIsPublic(portal.is_public);
      setColumns(String(portal.layout_config?.columns || 3) as '2' | '3' | '4');

      // Initialize branding state
      if (portal.branding) {
        setPortalBranding(portal.branding);
        setUseAccountBrandingToggle(false);
      } else {
        setUseAccountBrandingToggle(true);
        // Use account branding as the display value if available
        if (accountBranding) {
          setPortalBranding(accountBranding);
        }
      }
    }
  }, [portal, accountBranding]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleSave = () => {
    if (!portalId) return;

    const updates: UpdatePortalParams = {
      name,
      slug,
      description: description || undefined,
      welcome_message: welcomeMessage || undefined,
      is_active: isActive,
      is_public: isPublic,
      layout_config: {
        columns: parseInt(columns) as 2 | 3 | 4,
        showDescriptions: true,
      },
      // If using account branding, set to undefined (null in DB) to inherit
      // Otherwise save the custom branding
      branding: useAccountBrandingToggle ? undefined : portalBranding,
    };

    updatePortal({ portalId, updates }, {
      onSuccess: () => {
        setIsEditingBasic(false);
        setIsEditingLayout(false);
        setIsEditingBranding(false);
        toast.success('Portal updated successfully');
      }
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !portalId) return;

    const oldIndex = portalGenies.findIndex((g) => g.agent_id === active.id);
    const newIndex = portalGenies.findIndex((g) => g.agent_id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(portalGenies, oldIndex, newIndex);
      reorderGenies({
        portalId,
        orderedAgentIds: newOrder.map((g) => g.agent_id),
      });
    }
  };

  const handleAddGenie = (agentId: string) => {
    if (!portalId) return;
    addGenie({ portal_id: portalId, agent_id: agentId });
  };

  const handleRemoveGenie = (agentId: string) => {
    if (!portalId) return;
    removeGenie({ portalId, agentId });
  };

  const copyShareLink = async () => {
    if (!portal) return;
    try {
      const url = PortalService.generateShareUrl(portal);
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const copyEmbedCode = async () => {
    if (!portal) return;
    try {
      const code = PortalService.generateEmbedCode(portal);
      await navigator.clipboard.writeText(code);
      setCopiedEmbed(true);
      toast.success('Embed code copied to clipboard!');
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch {
      toast.error('Failed to copy embed code');
    }
  };

  // Filter out genies already in the portal and apply search
  const availableAgents = allAgents.filter((agent) => {
    const notInPortal = !portalGenies.some((pg) => pg.agent_id === agent.id);
    if (!notInPortal) return false;
    if (!genieSearchTerm) return true;
    const searchLower = genieSearchTerm.toLowerCase();
    return (
      agent.name?.toLowerCase().includes(searchLower) ||
      agent.description?.toLowerCase().includes(searchLower)
    );
  });

  if (portalLoading) {
    return <GenieLoader size="fullscreen" loadingText="Loading portal details…" />;
  }

  if (!portal) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Portal Not Found</h2>
          <p className="text-gray-600 mb-4">The portal you're looking for doesn't exist or you don't have access to it.</p>
          <button
            onClick={() => navigate('/genie-hub/portals')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Portals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1400px]">
        {/* Back Button */}
        <button
          onClick={() => navigate('/genie-hub/portals')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Portals</span>
        </button>

        {/* Header Section */}
        <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] mb-6">
          <div className="rounded-[14px] bg-white overflow-hidden">
            {/* Gradient Header Bar */}
            <div className="space-y-2 lg:relative p-2 lg:p-0 flex flex-col lg:flex-row items-center justify-between">
              <div
                className="lg:h-16 w-full"
                style={{
                  background: 'linear-gradient(135deg, #4E9CFF15, #FF4DB815)',
                }}
              />

              {/* Badges on gradient */}
              <div className="lg:absolute top-4 left-6 flex items-center flex-wrap gap-2">
                <span
                  className="px-3 py-1.5 rounded-full text-xs font-medium shadow-sm border bg-white"
                  style={{ color: brand, borderColor: `${brand}55` }}
                >
                  Portal
                </span>
                <div
                  className="px-3 py-1.5 rounded-full text-xs flex items-center shadow-sm border bg-white"
                  style={{ borderColor: `${brand}55`, color: brand }}
                >
                  <div className={`w-2 h-2 rounded-full mr-2 ${portal.is_active ? 'bg-[#4E9CFF]' : 'bg-gray-400'}`} />
                  <span className="font-medium">
                    {portal.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {portal.is_public && (
                  <div
                    className="px-3 py-1.5 rounded-full text-xs flex items-center shadow-sm border bg-white"
                    style={{ borderColor: `${brand}55`, color: brand }}
                  >
                    <span className="font-medium">Public</span>
                  </div>
                )}
              </div>
            </div>

            {/* Main Header Content */}
            <div className="px-6 md:px-8 py-6">
              <div className="flex flex-col md:flex-row items-start gap-6">
                {/* Name, Description, and Actions */}
                <div className="flex-1">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{portal.name}</h1>
                  <p className="text-base text-gray-500 mb-1">/portal/{portal.slug}</p>
                  {portal.description && (
                    <p className="text-base text-gray-600 mb-4">{portal.description}</p>
                  )}

                  {/* Quick Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => window.open(`/portal/${portal.slug}`, '_blank')}
                      className="order-2 lg:order-first w-full lg:w-auto flex items-center justify-center lg:justify-start gap-2 px-5 py-2.5 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
                      style={{
                        background: 'linear-gradient(135deg, #4E9CFF, #FF4DB8)',
                      }}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Preview Portal
                    </button>
                    <button
                      onClick={copyShareLink}
                      className="flex items-center gap-2 px-5 py-2.5 md:flex-1 lg:flex-none w-full lg:w-auto justify-center lg:justify-start bg-white border-2 rounded-xl font-semibold hover:bg-gray-50 transition-all hover:scale-105"
                      style={{ borderColor: brand, color: brand }}
                    >
                      {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copiedLink ? 'Copied!' : 'Copy Link'}
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isUpdating}
                      className="flex items-center gap-2 px-5 py-2.5 w-full lg:w-auto justify-center lg:justify-start bg-white border-2 rounded-xl font-semibold hover:bg-gray-50 transition-all hover:scale-105 disabled:opacity-50"
                      style={{ borderColor: brand, color: brand }}
                    >
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Sections */}
        <div className="space-y-4 lg:space-y-6">
          {/* Basic Settings Section */}
          <OverviewSectionCard
            title="Portal Details"
            subtitle="Configure your portal's name and description"
            genieIcon="play"
            brand={brand}
            onEdit={() => setIsEditingBasic(!isEditingBasic)}
          >
            {isEditingBasic ? (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Portal Name</label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Customer Support Hub"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">URL Slug</label>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-1">/portal/</span>
                      <Input
                        value={slug}
                        onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                        placeholder="customer-support"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Description</label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what this portal offers..."
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Welcome Message</label>
                  <Textarea
                    value={welcomeMessage}
                    onChange={(e) => setWelcomeMessage(e.target.value)}
                    placeholder="Welcome! Choose a genie below to get started..."
                    rows={2}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <OverviewSettingRow label="Name" value={<span className="font-semibold">{portal.name}</span>} />
                <OverviewSettingRow label="URL Slug" value={<span className="text-gray-600">/portal/{portal.slug}</span>} />
                {portal.description && (
                  <OverviewSettingRow label="Description" value={portal.description} fullWidth />
                )}
                {portal.welcome_message && (
                  <OverviewSettingRow
                    label="Welcome Message"
                    value={
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm italic">
                        "{portal.welcome_message}"
                      </div>
                    }
                    fullWidth
                  />
                )}
              </div>
            )}
          </OverviewSectionCard>

          {/* Layout & Visibility Section */}
          <OverviewSectionCard
            title="Layout & Visibility"
            subtitle="Control how your portal appears and who can access it"
            genieIcon="book-open"
            brand={brand}
            onEdit={() => setIsEditingLayout(!isEditingLayout)}
          >
            {isEditingLayout ? (
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Grid Columns</label>
                  <Select value={columns} onValueChange={(v) => setColumns(v as '2' | '3' | '4')}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 Columns</SelectItem>
                      <SelectItem value="3">3 Columns</SelectItem>
                      <SelectItem value="4">4 Columns</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Active</label>
                    <p className="text-sm text-gray-500">Portal is accessible to visitors</p>
                  </div>
                  <Switch checked={isActive} onCheckedChange={setIsActive} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Public</label>
                    <p className="text-sm text-gray-500">Anyone with the link can view</p>
                  </div>
                  <Switch checked={isPublic} onCheckedChange={setIsPublic} />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <OverviewSettingRow label="Grid Columns" value={`${portal.layout_config?.columns || 3} Columns`} />
                <OverviewSettingRow
                  label="Status"
                  value={
                    <Badge variant={portal.is_active ? 'default' : 'secondary'}>
                      {portal.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  }
                />
                <OverviewSettingRow
                  label="Visibility"
                  value={
                    <Badge variant={portal.is_public ? 'default' : 'secondary'}>
                      {portal.is_public ? 'Public' : 'Private'}
                    </Badge>
                  }
                />
              </div>
            )}
          </OverviewSectionCard>

          {/* Branding Section */}
          <OverviewSectionCard
            title="Branding"
            subtitle="Customize the portal's colors and appearance"
            genieIcon="book-open"
            brand={brand}
            onEdit={() => setIsEditingBranding(!isEditingBranding)}
          >
            {isEditingBranding ? (
              <div className="space-y-6">
                {/* Account Branding Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Use Account Branding</label>
                    <p className="text-sm text-gray-500">
                      {accountBranding
                        ? 'Inherit colors and logo from your account settings'
                        : 'Set up account branding in Account Settings first'}
                    </p>
                  </div>
                  <Switch
                    checked={useAccountBrandingToggle}
                    onCheckedChange={(checked) => {
                      setUseAccountBrandingToggle(checked);
                      if (checked && accountBranding) {
                        setPortalBranding(accountBranding);
                      }
                    }}
                    disabled={!accountBranding}
                  />
                </div>

                {/* Branding Editor */}
                {!useAccountBrandingToggle && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Customize branding specifically for this portal
                      </p>
                      {accountBranding && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setPortalBranding(accountBranding);
                            toast.success('Reset to account branding');
                          }}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Reset to Account
                        </Button>
                      )}
                    </div>
                    <BrandingEditor
                      branding={portalBranding}
                      onChange={setPortalBranding}
                    />
                  </div>
                )}

                {/* Preview when using account branding */}
                {useAccountBrandingToggle && accountBranding && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      This portal will use your account branding:
                    </p>
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-16 h-16">
                        <GenieAvatar
                          color={accountBranding.primaryColor}
                          secondaryColor={accountBranding.secondaryColor}
                          gradientEnabled={accountBranding.gradientEnabled}
                          gradientAngle={accountBranding.gradientAngle}
                          avatarBackgroundOpacity={accountBranding.avatarBackgroundOpacity}
                          customAvatarUrl={accountBranding.logoUrl || accountBranding.customAvatarUrl}
                          size={64}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className="w-6 h-6 rounded-full border border-gray-300"
                            style={{ backgroundColor: accountBranding.primaryColor }}
                            title="Primary color"
                          />
                          {accountBranding.secondaryColor && accountBranding.gradientEnabled && (
                            <div
                              className="w-6 h-6 rounded-full border border-gray-300"
                              style={{ backgroundColor: accountBranding.secondaryColor }}
                              title="Secondary color"
                            />
                          )}
                          <span className="text-sm text-gray-600">
                            {accountBranding.gradientEnabled ? 'Gradient enabled' : 'Solid color'}
                          </span>
                        </div>
                        {accountBranding.logoUrl && (
                          <span className="text-sm text-gray-500">Custom logo uploaded</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* No account branding message */}
                {useAccountBrandingToggle && !accountBranding && !accountBrandingLoading && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>No account branding configured.</strong> Set up your brand colors and logo in{' '}
                      <button
                        onClick={() => navigate('/account/settings')}
                        className="text-blue-600 underline hover:text-blue-800"
                      >
                        Account Settings
                      </button>{' '}
                      to use across all portals.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <OverviewSettingRow
                  label="Branding Source"
                  value={
                    portal.branding ? (
                      <Badge className="bg-[#FF4DB8]/10 text-[#FF4DB8] border border-[#FF4DB8]/20">
                        Custom Branding
                      </Badge>
                    ) : (
                      <Badge className="bg-[#4E9CFF]/10 text-[#4E9CFF] border border-[#4E9CFF]/20">
                        Account Branding
                      </Badge>
                    )
                  }
                />
                <OverviewSettingRow
                  label="Colors"
                  value={
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded-full border border-gray-300"
                        style={{ backgroundColor: (portal.branding || accountBranding || DEFAULT_BRANDING).primaryColor }}
                        title="Primary color"
                      />
                      {(portal.branding || accountBranding || DEFAULT_BRANDING).secondaryColor &&
                        (portal.branding || accountBranding || DEFAULT_BRANDING).gradientEnabled && (
                          <div
                            className="w-6 h-6 rounded-full border border-gray-300"
                            style={{ backgroundColor: (portal.branding || accountBranding || DEFAULT_BRANDING).secondaryColor || '' }}
                            title="Secondary color"
                          />
                        )}
                      <span className="text-sm text-gray-600">
                        {(portal.branding || accountBranding || DEFAULT_BRANDING).gradientEnabled
                          ? 'Gradient'
                          : 'Solid'}
                      </span>
                    </div>
                  }
                />
                {(portal.branding?.logoUrl || portal.branding?.customAvatarUrl || accountBranding?.logoUrl) && (
                  <OverviewSettingRow
                    label="Logo"
                    value={
                      <div className="w-10 h-10">
                        <GenieAvatar
                          color={(portal.branding || accountBranding || DEFAULT_BRANDING).primaryColor}
                          secondaryColor={(portal.branding || accountBranding)?.secondaryColor}
                          gradientEnabled={(portal.branding || accountBranding)?.gradientEnabled}
                          customAvatarUrl={portal.branding?.logoUrl || portal.branding?.customAvatarUrl || accountBranding?.logoUrl}
                          size={40}
                        />
                      </div>
                    }
                  />
                )}
              </div>
            )}
          </OverviewSectionCard>

          {/* Genies Section */}
          <OverviewSectionCard
            title="Genies"
            subtitle={`Select genies to include in this portal. ${portalGenies.length} genie${portalGenies.length !== 1 ? 's' : ''} added.`}
            genieIcon="chat"
            brand={brand}
          >
            <div className="space-y-6">
              {/* Selected Genies */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-3 block">
                  In This Portal ({portalGenies.length})
                </label>
                {geniesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : portalGenies.length > 0 ? (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext
                      items={portalGenies.map((g) => g.agent_id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {portalGenies.map((portalGenie) => (
                          <SortableGenieItem
                            key={portalGenie.id}
                            portalGenie={portalGenie}
                            onRemove={() => handleRemoveGenie(portalGenie.agent_id)}
                            isRemoving={isRemoving}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-center py-6 text-gray-500 border-2 border-dashed rounded-lg">
                    <p>No genies added yet. Select genies below to add them.</p>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t" />

              {/* Available Genies to Add */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">Available Genies</label>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={genieSearchTerm}
                      onChange={(e) => setGenieSearchTerm(e.target.value)}
                      placeholder="Search genies..."
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto">
                  {availableAgents.length > 0 ? (
                    availableAgents.map((agent) => {
                      const agentBrand = agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
                      return (
                        <button
                          key={agent.id}
                          onClick={() => handleAddGenie(agent.id)}
                          disabled={isAdding}
                          className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-100 hover:border-primary/50 hover:bg-gray-50 transition-all text-center group"
                        >
                          <div className="w-16 h-16 flex-shrink-0">
                            <GenieAvatar
                              color={agentBrand}
                              secondaryColor={agent.branding?.secondaryColor}
                              gradientEnabled={agent.branding?.gradientEnabled}
                              gradientAngle={agent.branding?.gradientAngle}
                              avatarBackgroundOpacity={agent.branding?.avatarBackgroundOpacity}
                              customAvatarUrl={agent.branding?.logoUrl || agent.branding?.customAvatarUrl}
                              size={64}
                            />
                          </div>
                          <div className="min-w-0 w-full">
                            <p className="font-medium text-gray-900 truncate text-sm">{agent.name}</p>
                            {agent.description && (
                              <p className="text-xs text-gray-500 truncate">{agent.description}</p>
                            )}
                          </div>
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <Plus className="w-5 h-5 text-primary" />
                          </div>
                        </button>
                      );
                    })
                  ) : genieSearchTerm ? (
                    <div className="col-span-full text-center py-8 text-gray-500">
                      <p>No genies found matching "{genieSearchTerm}"</p>
                    </div>
                  ) : (
                    <div className="col-span-full text-center py-8 text-gray-500">
                      <p>All your genies are already in this portal!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </OverviewSectionCard>

          {/* Share & Embed Section */}
          <OverviewSectionCard
            title="Share & Embed"
            subtitle="Get your portal link or embed code"
            genieIcon="share"
            brand={brand}
          >
            <div className="space-y-4">
              <OverviewSettingRow
                label="Portal Link"
                value={
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200 flex-1 truncate text-gray-700">
                      {PortalService.generateShareUrl(portal)}
                    </span>
                    <button
                      onClick={copyShareLink}
                      className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                      title="Copy link"
                    >
                      {copiedLink ? (
                        <Check className="w-4 h-4 text-[#4E9CFF]" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                  </div>
                }
                fullWidth
              />
              <OverviewSettingRow
                label="Embed Code"
                value={
                  <Button variant="outline" onClick={copyEmbedCode} className="w-full">
                    {copiedEmbed ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copiedEmbed ? 'Copied!' : 'Copy Embed Code'}
                  </Button>
                }
                fullWidth
              />
            </div>
          </OverviewSectionCard>
        </div>
      </div>
    </div>
  );
};

// Sortable Genie Item Component
interface SortableGenieItemProps {
  portalGenie: PortalGenie;
  onRemove: () => void;
  isRemoving: boolean;
}

const SortableGenieItem = ({ portalGenie, onRemove, isRemoving }: SortableGenieItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: portalGenie.agent_id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const name = portalGenie.display_name || portalGenie.agent?.name || 'Genie';
  const brand = portalGenie.agent?.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </button>

      <div className="w-10 h-10 flex-shrink-0">
        <GenieAvatar
          color={brand}
          secondaryColor={portalGenie.agent?.branding?.secondaryColor}
          gradientEnabled={portalGenie.agent?.branding?.gradientEnabled}
          gradientAngle={portalGenie.agent?.branding?.gradientAngle}
          avatarBackgroundOpacity={portalGenie.agent?.branding?.avatarBackgroundOpacity}
          customAvatarUrl={portalGenie.custom_avatar_url || portalGenie.agent?.branding?.logoUrl || portalGenie.agent?.branding?.customAvatarUrl}
          size={40}
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">{name}</p>
        {portalGenie.is_featured && (
          <Badge variant="secondary" className="mt-1">Featured</Badge>
        )}
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={isRemoving}
        className="text-gray-400 hover:text-red-600"
      >
        {isRemoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
      </Button>
    </div>
  );
};

export default PortalDetailPage;
